<template>
  <aside id="offcanvas-sidemenu" class="app-sidebar -translate-x-full xl:translate-x-0" aria-label="Sidebar">
    <div id="logo-cont" class="app-sidebar-logo-container">
      <RouterLink :to="ROUTER.CUSTOMER.HOME_PAGE.PATH">
        <a href="javascript:void(0);" data-drawer-hide="offcanvas-sidemenu">
          <img src="../../assets/branch-logo.svg" class="app-sidebar-logo" alt="Digital Hands Logo" />
        </a>
      </RouterLink>
    </div>
    <div id="menu-cont" class="app-sidebar-menu-container">
      <SideMenu side-menu-id="offcanvas-sidemenu" :items="menuItems" />
    </div>
    <div
      v-if="
        hasAccess(authUser, {
          ...ROUTER.CUSTOMER.CONTACT_US_PAGE.accessPolicy,
          scope: PermissionScopeEnum.self,
        })
      "
      id="cta-support-cont"
      class="flex absolute bottom-0 left-0 z-20 justify-center px-5 pt-3 pb-6 space-x-4 w-full dark:bg-container"
    >
      <div id="dropdown-cta" class="w-full rounded-lg" role="alert">
        <div class="flex mb-3 text-sm text-blue-800 dark:text-text">
          <LifeSaverIcon class="app-sidebar-menu-list-icon" />
          <span class="inline-flex items-center ml-2">Need support?</span>
          <div class="clear-both"></div>
        </div>
        <div class="text-center text-xs font-medium">
          <RouterLink :to="ROUTER.CUSTOMER.CONTACT_US_PAGE.PATH">
            <a
              class="inline-flex items-center justify-center w-full py-2 px-5 text-sm font-medium focus:outline-none bg-green-700 rounded-lg border border-green-700"
              href="javascript:void(0);"
              data-drawer-hide="offcanvas-sidemenu"
            >
              <PhoneIcon class="app-sidebar-menu-list-icon !w-3 !h-3 mr-2" :viewBox="`0 0 12 12`" />
              <span>Contact us</span>
            </a>
          </RouterLink>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, onUnmounted, Raw, ref, watch } from 'vue';
import { isEmpty } from 'lodash';
import { RouterLink } from 'vue-router';
import { initFlowbite } from 'flowbite';
import HomeIcon from '@/components/icons/HomeIcon.vue';
import ColumnIcon from '@/components/icons/ColumnIcon.vue';
import FileIcon from '@/components/icons/FileIcon.vue';
import InboxIcon from '@/components/icons/InboxIcon.vue';
import LinkIcon from '@/components/icons/LinkIcon.vue';
import KeyIcon from '@/components/icons/KeyIcon.vue';
import GridIcon from '@/components/icons/GridIcon.vue';
import PhoneIcon from '@/components/icons/PhoneIcon.vue';
import LifeSaverIcon from '@/components/icons/LifeSaverIcon.vue';
import TicketIcon from '@/components/icons/TicketIcon.vue';
import UsersIcon from '@/components/icons/UsersIcon.vue';
import FilePdfIcon from '@/components/icons/FilePdfIcon.vue';
import FileInvoiceIcon from '@/components/icons/FileInvoiceIcon.vue';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import { RouteItem, ROUTER } from '../../app-constants/router.constant';
import { SideMenuItem } from '@/entities/sideMenuItem.entity';
import SideMenu from './SideMenu.vue';
import { hasAccess, hasMultitenancyAccess } from '@/utils/user.utils';
import { PermissionScopeEnum } from '@/entities/role.entity';
import { ECisoModuleTabs } from '@/views/customer-dashboard-CISO/ciso.helpers';
import { EFortiAnalyzerModuleTabs } from '@/views/customer-dashboard-fortianalyzer/fortianalyzer.helpers';
import { EPromptAIModuleTabs } from '@/views/customer-dashboard-prompt-ai/promptAI.helpers';

import useMultitenancy from '@/hooks/useMultitenancy';

import { useModuleStateStore } from '@/stores/moduleState.store';
import { useAccountInformation } from '@/hooks/useAccountInformation';

const impersonationInfo = useImpersonationInfo();
const {
  hasTechnologyChronicle,
  hasProductSIEM,
  hasProductEDR,
  hasTechnologySentinelOne,
  hasTechnologyFortiAnalyzer,
  isSuccess: fetchingAccountDataSuccess,
} = useAccountInformation();
let authUser = impersonationInfo.impersonatedUser;

const {
  setSelectedTenant,
  selectedTenant,
  hasRelatedReadPermission,
  hasSelfReadPermission,

  isLoadingRelatedTenants,
  isRelatedTenantsFullyLoaded,

  fetchRelatedTenants,
  currentSelectedTenant,
  tenantDropdownOptions,
} = useMultitenancy({
  includeSoldProducts: true,
});

const { getTenantSelection } = useModuleStateStore();

const shouldShowOperationalMetrics = ref<boolean>(false);
const shouldShowCisoPerimeter = ref<boolean>(false);

watch(
  [
    (): unknown => currentSelectedTenant.value,
    (): unknown => selectedTenant.value,
    (): unknown => getTenantSelection(),
    (): unknown => fetchingAccountDataSuccess.value,
  ],
  () => {
    if (fetchingAccountDataSuccess.value) {
      const seletectTenantId = getTenantSelection();

      const isSelfTenantCase = seletectTenantId
        ? seletectTenantId === authUser.tenantId
        : currentSelectedTenant.value?.id === authUser?.tenantId;

      if (isSelfTenantCase) {
        shouldShowOperationalMetrics.value =
          (hasTechnologyChronicle.value && hasProductSIEM.value) ||
          (hasTechnologySentinelOne.value && hasProductEDR.value);
        shouldShowCisoPerimeter.value = hasTechnologyFortiAnalyzer.value;
      } else {
        const foundTenant = tenantDropdownOptions.value?.find((t) => t.id === seletectTenantId);
        if (!isEmpty(foundTenant)) {
          shouldShowOperationalMetrics.value =
            (foundTenant?.soldProducts?.hasChronicle && foundTenant?.soldProducts?.hasSiem) ||
            (foundTenant?.soldProducts?.hasSentinelone && foundTenant?.soldProducts?.hasEdr);
          shouldShowCisoPerimeter.value = !!foundTenant?.soldProducts?.hasFortianalyzer;
        }
      }
    }
  },
);

const menuItems = computed<SideMenuItem[]>(() => {
  const items: SideMenuItem[] = [];

  const addMenuItem = (route: RouteItem, icon?: Raw<object>, children?: SideMenuItem[]): void => {
    if (hasMultitenancyAccess(authUser, route.accessPolicy)) {
      items.push({
        id: route.KEY,
        name: route.NAME,
        link: isEmpty(children) ? route.PATH : '',
        icon: icon ? markRaw(icon) : undefined,
        children: children,
      });
    }
  };

  // --- Build Main Menu Items ---
  addMenuItem(ROUTER.CUSTOMER.HOME_PAGE, HomeIcon);
  addMenuItem(ROUTER.CUSTOMER.MY_CASES_PAGE, TicketIcon);

  // --- Build Dashboard Items (with CISO Sub-items) ---
  const dashboardChildren: SideMenuItem[] = [];
  const OPERATION_METRIC_SIDEBAR_INDEX = 1;

  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.accessPolicy)) {
    const cisoChildren: SideMenuItem[] = [
      {
        id: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.SecurityPosture}`,
        name: 'Security Posture',
        link: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.SecurityPosture}`,
      },
      {
        id: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.Compliance}`,
        name: 'Compliance',
        link: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.Compliance}`,
      },
    ];

    // Use the computed property directly here
    if (shouldShowOperationalMetrics.value) {
      cisoChildren.splice(OPERATION_METRIC_SIDEBAR_INDEX, 0, {
        id: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.OperationalMetric}`,
        name: 'Operational Metrics',
        link: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.OperationalMetric}`,
      });
    }

    // Gated like the Perimeter tab itself: FortiAnalyzer product required
    if (shouldShowCisoPerimeter.value) {
      cisoChildren.push({
        id: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.Perimeter}`,
        name: 'Perimeter',
        link: `${ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.Perimeter}`,
      });
    }

    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.NAME,
      link: '', // No direct link for parent with children
      children: cisoChildren,
    });
  }

  // --- Fortinet FortiAnalyzer (Traffic/Security sub-items) ---
  // Visibility follows the FortiAnalyzer module permission granted to the role, not the tenant's purchased product.
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.NAME,
      link: '', // No direct link for parent with children
      children: [
        {
          id: `${ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.KEY}-${EFortiAnalyzerModuleTabs.Traffic}`,
          name: 'Traffic',
          link: `${ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.PATH}?tab=${EFortiAnalyzerModuleTabs.Traffic}`,
        },
        {
          id: `${ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.KEY}-${EFortiAnalyzerModuleTabs.Security}`,
          name: 'Security',
          link: `${ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.PATH}?tab=${EFortiAnalyzerModuleTabs.Security}`,
        },
      ],
    });
  }

  // --- Other dashboard sub-items ---
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.NAME,
      link: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.PATH,
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY,
      name: ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.NAME,
      link: '',
      children: [
        {
          id: `${ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY}-${EPromptAIModuleTabs.EmployeesUsage}`,
          name: 'Employees AI usage',
          link: `${ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.PATH}?tab=${EPromptAIModuleTabs.EmployeesUsage}`,
        },
        {
          id: `${ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY}-${EPromptAIModuleTabs.ActivityLog}`,
          name: 'Activity log',
          link: `${ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.PATH}?tab=${EPromptAIModuleTabs.ActivityLog}`,
        },
      ],
    });
  }

  // Add the dashboards parent item if it has any children
  if (dashboardChildren.length > 0) {
    addMenuItem(ROUTER.CUSTOMER.DASHBOARDS_PAGE, ColumnIcon, dashboardChildren);
  }

  // --- Other Main Menu Items ---
  addMenuItem(ROUTER.CUSTOMER.FILES_PAGE, FileIcon);
  addMenuItem(ROUTER.CUSTOMER.ACTIVITIES_PAGE, InboxIcon);
  addMenuItem(ROUTER.CUSTOMER.SERVICE_PAGE, GridIcon);
  addMenuItem(ROUTER.CUSTOMER.MY_LINKS_PAGE, LinkIcon);
  addMenuItem(ROUTER.CUSTOMER.SECRET_MANAGEMENT_PAGE, KeyIcon);
  addMenuItem(ROUTER.CUSTOMER.USER_MANAGEMENT_PAGE, UsersIcon);

  // --- PDF Reports ---
  if (hasMultitenancyAccess(authUser, ROUTER.REPORT_SCHEDULES_PAGE.accessPolicy)) {
    items.push({
      id: ROUTER.REPORT_SCHEDULES_PAGE.KEY,
      name: ROUTER.REPORT_SCHEDULES_PAGE.NAME,
      link: ROUTER.REPORT_SCHEDULES_PAGE.PATH,
      icon: markRaw(FilePdfIcon),
    });
  }
  if (hasMultitenancyAccess(authUser, ROUTER.REPORT_HISTORY_PAGE.accessPolicy)) {
    items.push({
      id: ROUTER.REPORT_HISTORY_PAGE.KEY,
      name: ROUTER.REPORT_HISTORY_PAGE.NAME,
      link: ROUTER.REPORT_HISTORY_PAGE.PATH,
      icon: markRaw(FileInvoiceIcon),
    });
  }

  return items;
});

onMounted(() => {
  resizeSideMenu();
  initFlowbite();
  window.addEventListener('resize', resizeSideMenu);
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeSideMenu);
});

const resizeSideMenu = (): void => {
  const offCanvasSideMenu = document.getElementById('offcanvas-sidemenu');
  const logoView = document.getElementById('logo-cont');
  const menuView = document.getElementById('menu-cont');
  const suppotCTAView = document.getElementById('cta-support-cont');
  const logoMargin = 24;
  if (!offCanvasSideMenu || !logoView || !menuView || !suppotCTAView) return;
  menuView.style.height =
    offCanvasSideMenu.offsetHeight - (logoView.offsetHeight + logoMargin + suppotCTAView.offsetHeight) + 'px';
};

watch(
  isLoadingRelatedTenants,
  () => {
    if (!hasRelatedReadPermission.value) {
      return;
    }

    if (!isLoadingRelatedTenants.value && isRelatedTenantsFullyLoaded.value) {
      setSelectedTenant();
    }
  },
  {
    immediate: true,
  },
);

onMounted(() => {
  if (hasSelfReadPermission.value && !hasRelatedReadPermission.value) {
    setSelectedTenant();
  }

  if (hasRelatedReadPermission.value) {
    fetchRelatedTenants();
  }
});
</script>
